import type { Entity, Persisted } from '@tunasong/schemas'
import type { FC } from 'react'
import { Typography } from '../primitives/index.js'
import { useParentChain } from '../navigation/parent-chain.js'
import styles from './entity-card.module.css'

export interface CardParentSpaceProps {
  entity: Persisted<Entity>
}

/**
 * Muted inline "which space" chip for grid cards. The "space" is the topmost
 * *named* ancestor (the chain is root-first), excluding the entity itself.
 *
 * Why not simply `parentChain[0]`: the chain includes the leaf entity as its
 * last element, and its root can be an unnamed/untitled container — or, while
 * ancestors are still loading (and for items that live at the root with no
 * space), the chain collapses to just `[entity]`. Taking `[0]` blindly then
 * renders either the item's own name or an empty name followed by the bare
 * "·" separator. Selecting the topmost element that (a) isn't the entity and
 * (b) actually has a name yields the real space, or nothing at all.
 *
 * This is the only place `useParentChain` runs for cards: it is mount-gated by
 * `EntityCard`'s `showParentSpace` prop, so cards that don't opt in never run
 * the ancestor lookup.
 */
export const CardParentSpace: FC<CardParentSpaceProps> = props => {
  const { entity } = props
  const parentChain = useParentChain(entity)
  const space = parentChain.find(ancestor => ancestor.id !== entity.id && Boolean(ancestor.name))

  if (!space) {
    return null
  }

  return (
    <>
      <Typography variant="caption" component="span" className={styles.parentSpace} title={space.name}>
        {space.name}
      </Typography>
      {/* Trailing separator is owned by this component so it only renders when a space exists.
       * Decorative + aria-hidden: a screen reader reads "<space> <time>" as a natural pause,
       * never an orphan "·" when the entity sits at a space root. */}
      <Typography variant="caption" component="span" aria-hidden="true">
        {' · '}
      </Typography>
    </>
  )
}

export default CardParentSpace
